import React, { useEffect, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile widget.
 *
 * Renders the challenge and hands the resulting token back via onVerify.
 * The token is single-use and expires after ~5 minutes, so this component
 * resets itself on expiry and exposes a reset handle for use after a failed
 * submit (a rejected token cannot be replayed).
 *
 * Set REACT_APP_TURNSTILE_SITE_KEY to the widget's *site key* (the public
 * one). The secret key belongs only on the server - never ship it here.
 *
 * If no site key is configured this renders nothing and reports an empty
 * token, so local development and preview builds keep working - the backend
 * likewise skips verification when it has no secret key.
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

/** Load the Turnstile script once, shared across every widget instance. */
let scriptPromise = null;

/**
 * `script.onload` firing does not guarantee `window.turnstile` is assigned
 * yet. Without this poll the render call can silently no-op, leaving the user
 * with no widget, no token, and - once verification is enforced - a signup
 * that can never succeed. Wait for the global to actually appear.
 */
function waitForTurnstile(timeoutMs = 5000) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    (function poll() {
      if (window.turnstile) return resolve();
      if (Date.now() - startedAt > timeoutMs) {
        return reject(new Error('window.turnstile never appeared'));
      }
      setTimeout(poll, 50);
    })();
  });
}

function loadTurnstileScript() {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => waitForTurnstile().then(resolve, reject));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => waitForTurnstile().then(resolve, reject);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  // A failed load must not be cached forever - let a remount retry.
  scriptPromise.catch(() => {
    scriptPromise = null;
  });

  return scriptPromise;
}

const TurnstileWidget = ({
  onVerify,
  action = 'submit',
  theme = 'light',
  className = '',
  resetSignal = 0,
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const [failed, setFailed] = useState(false);

  // Keep the latest callback without re-rendering the widget, which would
  // force the user to solve the challenge again on every parent state change.
  useEffect(() => {
    onVerifyRef.current = onVerify;
  }, [onVerify]);

  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const userLang = navigator.language || navigator.userLanguage;

  useEffect(() => {
    if (!siteKey || !containerRef.current) return undefined;

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile || !containerRef.current) return;

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme,
          callback: (token) => onVerifyRef.current && onVerifyRef.current(token),
          'expired-callback': () => onVerifyRef.current && onVerifyRef.current(''),
          'error-callback': () => {
            setFailed(true);
            if (onVerifyRef.current) onVerifyRef.current('');
          },
        });
      })
      .catch(() => {
        // Script blocked (ad blocker, offline, network policy). The backend
        // decides what to do about a missing token; do not trap the user here.
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          /* widget already gone */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, action, theme]);

  // Parent bumps resetSignal after a rejected submit: the old token is spent
  // and Cloudflare will reject it a second time as `timeout-or-duplicate`.
  useEffect(() => {
    if (resetSignal > 0 && widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (e) {
        /* nothing to reset */
      }
    }
  }, [resetSignal]);

  if (!siteKey) return null;

  return (
    <div className={className}>
      <div ref={containerRef} />
      {failed && (
        <p className="mt-2 text-sm text-gray-500">
          {userLang === 'zh-CN'
            ? '验证组件加载失败，请刷新页面重试。'
            : 'The verification widget could not load. Please refresh and try again.'}
        </p>
      )}
    </div>
  );
};

export default TurnstileWidget;
